import { useDispatch, useSelector } from 'react-redux';
import React, { useState, createRef, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  SideMenu,
  RichTextSection,
} from 'design-comuni-plone-theme/components/ItaliaTheme/View';
import { Pagination } from 'design-comuni-plone-theme/components/ItaliaTheme';
import { SelectInput } from 'design-comuni-plone-theme/components';

import { monthsOptionsFunction } from '@redturtle/volto-io-prenoto/helpers';
import {
  getAvailableSlots,
  getBookingSchema,
} from '@redturtle/volto-io-prenoto/actions/booking';
import {
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Progress,
} from 'design-react-kit';
import moment from 'moment';
import {
  PrenotazioneCardIndirizzoOrario,
  PrenotazioneStepperActions,
} from '@redturtle/volto-io-prenoto/components/View/PrenotazioneView';

const messages = defineMessages({
  appointment_title: {
    id: 'booking_appointment_title',
    defaultMessage: 'Appuntamenti disponibili',
  },
  ufficio_title: {
    id: 'booking_ufficio_title',
    defaultMessage: 'Agenda',
  },
  month_select_label: {
    id: 'booking_month_select_label',
    defaultMessage: 'Seleziona un mese',
  },
  folder_address: {
    id: 'booking_folder_address',
    defaultMessage: 'Indirizzo',
  },
  folder_opening: {
    id: 'booking_folder_opening',
    defaultMessage: 'Apertura',
  },
  folder_date_legend: {
    id: 'booking_folder_date_legend',
    defaultMessage: 'Seleziona un giorno e orario',
  },
  date_hours: {
    id: 'booking_date_hours',
    defaultMessage: 'ore',
  },
  progressbar_label: {
    id: 'booking_progressbar_label',
    defaultMessage: 'In elaborazione...',
  },
  no_slots: {
    id: 'booking_no_slots',
    defaultMessage: 'Non ci sono slot disponibili per il mese selezionato',
  },
  unbookable_types: {
    id: 'booking_unbookable_types',
    defaultMessage: 'Non è possibile prenotare per',
  },
});

const PrenotazioneDataEOra = ({
  prenotazioneObj,
  setPrenotazioneObj,
  activeStep,
  setActiveStep,
  scrollToTop,
  params,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const folder = prenotazioneObj.booking_folder;

  // variables for months options
  moment.locale(intl.locale);
  const { MONTHS } = monthsOptionsFunction();
  const dateStart = moment();
  const selectedMonth =
    prenotazioneObj?.booking_month?.value &&
    `${prenotazioneObj?.booking_month?.value}-01`;
  // date list
  const availableSlots = useSelector((state) => state.availableSlots);
  const bookingSchema = useSelector((state) => state.bookingSchema?.result);
  const [dates, setDates] = useState(null); // lasciamo null e non array vuoto perchè il messaggio di "nessuno slot disponibile" apparirebbe anche senza aver fatto la chiamata

  // SIDEMENU
  let documentBody = createRef();
  const [sideMenuElements, setSideMenuElements] = useState(null);

  // Data display in pagination range
  const page_size = 5;
  const [currentListData, setCurrentListData] = useState([]);

  // Triggered when pagination change
  const paginationChange = (e, { activePage }) => {
    resetActivePage();
    const current = activePage?.children ?? 1;
    setPrenotazioneObj({
      ...prenotazioneObj,
      currentPageDates: current,
    });
  };

  /* RESET PAGE ACTIVE (pagination) */
  const resetActivePage = (forceSet = false) => {
    const paginationItems = document.querySelectorAll(
      'ul.pagination li.page-item',
    );
    paginationItems.forEach((item, index) => {
      item.querySelector('button').removeAttribute('aria-current');
      if (forceSet && index === 1) {
        item.querySelector('button').setAttribute('aria-current', 'page');
      }
    });
  };

  useEffect(() => {
    if (documentBody.current) {
      if (__CLIENT__) {
        setSideMenuElements(documentBody.current);
      }
    }
  }, [documentBody]);

  /* INIT MONTHS SELECT WITH CURRENT MONTH */
  useEffect(() => {
    if (!prenotazioneObj.booking_month) {
      setPrenotazioneObj({
        ...prenotazioneObj,
        booking_month: {
          value: dateStart.format('YYYY-MM'),
          label: dateStart.format('MMMM'),
        },
      });
    }
  }, []);

  useEffect(() => {
    setDates(null);
    if (!availableSlots.loading && selectedMonth) {
      dispatch(
        getAvailableSlots(
          folder['@id'],
          selectedMonth,
          params.booking_type || '',
        ),
      );
    }
  }, [folder, selectedMonth, params.booking_type]);

  /* GET DATES RESULTS */
  useEffect(() => {
    if (availableSlots?.result?.items) {
      setDates(availableSlots?.result?.items);
    } else {
      setDates(null);
    }
  }, [availableSlots]);

  /* GET DATES RESULTS */
  useEffect(() => {
    if (dates && dates.length > 0) {
      setCurrentListData(dates.slice(0, page_size));
    } else if (dates?.length === 0 || !dates) {
      setCurrentListData([]);
    }
  }, [dates]);

  /* CHANGE PAGINATION WIDGET */
  useEffect(() => {
    if (dates && dates.length > 0) {
      const firstPageIndex = (prenotazioneObj.currentPageDates - 1) * page_size;
      const lastPageIndex = firstPageIndex + page_size;
      setCurrentListData(dates.slice(firstPageIndex, lastPageIndex));
    }
  }, [dates, prenotazioneObj.currentPageDates]);

  /* ONCHANGE SELECT MONTHS */
  const handleChangeMonthSelect = (selectedOption) => {
    setPrenotazioneObj({
      ...prenotazioneObj,
      booking_month: selectedOption,
      booking_date: null,
      currentPageDates: 1,
    });
    resetActivePage(true);
  };

  /* GET PRENOTAZIONE SCHEMA */
  const doGetBoookingSchema = (date) => {
    dispatch(getBookingSchema(prenotazioneObj.booking_folder?.['@id'], date));
  };

  /* ONCHANGE RADIO DATES */
  const handleChangeDate = (e) => {
    setPrenotazioneObj({
      ...prenotazioneObj,
      booking_date: e.target.value,
    });

    doGetBoookingSchema(e.target.value);
  };

  return (
    <>
      {/* MENU LATERALE */}
      <aside aria-hidden={true} className="col-12 col-lg-3">
        {__CLIENT__ && (
          <SideMenu
            data={sideMenuElements}
            content_uid={'prenotazioneDataEOra'}
          />
        )}
      </aside>
      <section
        id="main-content-section"
        className="prenotazioneDataEOra section-form col-12 col-lg-8 offset-lg-1 mt-3"
        ref={documentBody}
      >
        <RichTextSection
          title={intl.formatMessage(messages.appointment_title)}
          tag_id="prenotazioneDataEOra_section"
          show_title={false}
        >
          <Card className="section-form--card mb-0 no-after">
            {/* SELECT SELEZIONA MESE */}
            <h2 className="mb-4" aria-hidden={true}>
              {intl.formatMessage(messages.appointment_title) + '*'}
            </h2>
            <label htmlFor="office-choice" className="visually-hidden">
              {intl.formatMessage(messages.month_select_label)}
            </label>
            <SelectInput
              id="office-choice"
              placeholder={intl.formatMessage(messages.month_select_label)}
              aria-label={intl.formatMessage(messages.month_select_label)}
              value={prenotazioneObj.booking_month}
              options={MONTHS}
              onChange={handleChangeMonthSelect}
            />

            {/* RADIO DATE PER GLI APPUNTAMENTI */}
            <Form className="mt-4 bg-white">
              {/* PROGRESS BAR DI CARICAMENTO DATE */}
              {availableSlots.loading && (
                <Progress
                  indeterminate
                  label={intl.formatMessage(messages.progressbar_label)}
                />
              )}
              <fieldset>
                <legend className="visually-hidden">
                  {intl.formatMessage(messages.folder_date_legend)}
                </legend>

                {currentListData.length > 0 &&
                  currentListData.map((date) => (
                    <FormGroup
                      check
                      className="fw-semibold border-bottom border-light py-3 mx-3"
                      key={date}
                    >
                      <Input
                        name="dateGroup"
                        type="radio"
                        id={date}
                        value={date}
                        checked={date === prenotazioneObj.booking_date}
                        onChange={(e) => handleChangeDate(e)}
                      />
                      <Label check htmlFor={date}>
                        <span className="text-capitalize">
                          {moment(date).format('dddd')}{' '}
                          {moment(date).format('DD MMMM YYYY')}
                        </span>{' '}
                        {intl.formatMessage(messages.date_hours)}{' '}
                        {moment(date).format('HH:mm')}
                        {date === bookingSchema?.date &&
                          bookingSchema?.booking_types?.unbookable?.length >
                            0 && (
                            <div className="unbookable-types">
                              {intl.formatMessage(messages.unbookable_types)}:{' '}
                              {bookingSchema.booking_types.unbookable.map(
                                (ub, ub_index) => (
                                  <span>
                                    {ub.name}
                                    {ub_index <
                                    bookingSchema.booking_types.unbookable
                                      .length -
                                      1
                                      ? ', '
                                      : ''}
                                  </span>
                                ),
                              )}
                            </div>
                          )}
                      </Label>
                    </FormGroup>
                  ))}
              </fieldset>

              {dates && dates.length === 0 && (
                <p className="mt-3 mx-2">
                  {intl.formatMessage(messages.no_slots)}
                </p>
              )}
            </Form>

            {dates && dates.length > page_size && (
              <Pagination
                activePage={prenotazioneObj.currentPageDates}
                totalPages={Math.ceil(dates.length / page_size)}
                onPageChange={paginationChange}
              />
            )}
          </Card>
        </RichTextSection>

        {folder && (
          <RichTextSection
            title={intl.formatMessage(messages.ufficio_title)}
            tag_id="prenotazioneDataEOra_ufficio_section"
            show_title={false}
          >
            <Card className="section-form--card mb-0 no-after">
              {/* INFO UFFICIO */}
              <h2 className="mb-3">
                {intl.formatMessage(messages.ufficio_title)}
              </h2>
              <div className="bg-white p-3 mb-4">
                <CardHeader className="px-0">
                  <h3 className="fw-semibold">{folder.title}</h3>
                </CardHeader>
                <CardBody className="p-0 pt-3 radio-card--info">
                  <PrenotazioneCardIndirizzoOrario
                    folder={folder}
                    address={folder?.address}
                  />
                </CardBody>
              </div>
            </Card>
          </RichTextSection>
        )}
      </section>

      {/* BUTTONS */}
      <PrenotazioneStepperActions
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        disabled={!prenotazioneObj.booking_date}
        scrollToTop={scrollToTop}
      />
    </>
  );
};

export default PrenotazioneDataEOra;
